import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  FormattedMessage,
  FormattedHTMLMessage,
  defineMessages,
  injectIntl,
} from 'react-intl';
import classNames from 'classnames';
import Autosuggest from 'react-autosuggest';
import MathJax from 'react-mathjax2';

import Tag from '../../../models/Tag';

import { LOCALES } from '../../common/enums';
import {
  CompetitionPropType,
  SeriesPropType,
  QuestionPropType,
  ChoicePropType,
  TagPropType,
  PaginationPropType,
} from '../../common/types';

import LocaleSelector from '../../common/LocaleSelector';
import Spinner from '../../common/Spinner';
import Pagination from '../../common/Pagination';

export const PER_PAGE = 20;
const AUGMENTED_PREFIX = 'augmented';
const POINTS = [3, 4, 5];

const messages = defineMessages({
  suggestionPlaceholder: {
    id: 'home-archive-search-tag-suggestion-placeholder',
    defaultMessage: 'Hae avainsanoja',
  },
});

class Search extends Component {
  static propTypes = {
    intl: PropTypes.object.isRequired,
    fetchQuestions: PropTypes.func.isRequired,
    fetchStats: PropTypes.func.isRequired,
    questionLocale: PropTypes.string.isRequired,
    competitions: PropTypes.arrayOf(CompetitionPropType).isRequired,
    serieses: PropTypes.arrayOf(SeriesPropType).isRequired,
    questions: PropTypes.arrayOf(QuestionPropType).isRequired,
    questionsPagination: PaginationPropType,
    questionsLoading: PropTypes.bool.isRequired,
    choices: PropTypes.arrayOf(ChoicePropType).isRequired,
    tags: PropTypes.arrayOf(TagPropType).isRequired,
    stats: PropTypes.object.isRequired,
    printQuestions: PropTypes.array.isRequired,
    onToggleShouldPrint: PropTypes.func.isRequired,
    onClearPrintQuestions: PropTypes.func.isRequired,
    onPrint: PropTypes.func.isRequired,
    onPrintSolutions: PropTypes.func.isRequired,
  };

  static defaultProps = {
    questionsPagination: null,
  };

  state = {
    selectedTags: [],
    tagInputValue: '',
    page: 1,
    expandedQuestions: [],
    showQuestionAnswers: [],
    showQuestionStats: [],
  };

  augmentedTags = () => {
    const { competitions, serieses } = this.props;
    const tags = [];

    competitions.forEach(competition => {
      let { name } = competition;

      // Show only year for competition tags if they have a year at the end
      if (name.match(/[0-9]{4}$/)) {
        name = name.substr(-4);
      }

      tags.push(
        new Tag({
          id: `${AUGMENTED_PREFIX}-competition-${competition.id}`,
          name: LOCALES.reduce(
            (acc, val) => Object.assign(acc, { [val]: name }),
            {}
          ),
        })
      );
    });

    Object.keys(
      serieses.reduce((acc, val) => Object.assign(acc, { [val.name]: val }), {})
    ).forEach(series => {
      tags.push(
        new Tag({
          id: `${AUGMENTED_PREFIX}-series-${series}`,
          name: LOCALES.reduce(
            (acc, val) => Object.assign(acc, { [val]: series }),
            {}
          ),
        })
      );
    });

    POINTS.forEach(points => {
      tags.push(
        new Tag({
          id: `${AUGMENTED_PREFIX}-points-${points}`,
          name: LOCALES.reduce(
            (acc, val) => Object.assign(acc, { [val]: `${points} p` }),
            {}
          ),
        })
      );
    });

    return tags;
  };

  onSelectTag = tagId => {
    const { selectedTags } = this.state;

    const newSelectedTags = selectedTags.includes(tagId)
      ? selectedTags.filter(t => t !== tagId)
      : [...selectedTags, tagId];

    this.setState({ selectedTags: newSelectedTags, page: 1 }, () => {
      this.onSearch();
    });
  };

  onTagSuggestionsClearRequested = () => {
    this.setState({ tagInputValue: '' });
  };

  onTagSuggestionSelected = (event, { suggestion }) => {
    this.onSelectTag(suggestion.id);
  };

  onTagInputChange = (event, { newValue }) => {
    this.setState({ tagInputValue: newValue });
  };

  onSearch = () => {
    const { selectedTags, page } = this.state;

    const tagIds = selectedTags.filter(
      tagId => !tagId.startsWith(AUGMENTED_PREFIX)
    );

    const competitionIds = selectedTags
      .filter(tagId => tagId.startsWith(`${AUGMENTED_PREFIX}-competition`))
      .map(tagId => tagId.substring(tagId.split('-', 2).join('-').length + 1));

    const seriesNames = selectedTags
      .filter(tagId => tagId.startsWith(`${AUGMENTED_PREFIX}-series`))
      .map(tagId => tagId.substring(tagId.split('-', 2).join('-').length + 1));

    const points = selectedTags
      .filter(tagId => tagId.startsWith(`${AUGMENTED_PREFIX}-points`))
      .map(tagId =>
        parseFloat(tagId.substring(tagId.split('-', 2).join('-').length + 1))
      );

    this.setState({ expandedQuestions: [], showQuestionAnswers: [] });
    this.props.fetchQuestions({
      competition_ids: competitionIds,
      series_names: seriesNames,
      tag_ids: tagIds,
      points,
      include: 'choices',
      limit: PER_PAGE,
      page,
    });
  };

  onPageChange = value => {
    this.setState({ page: value.selected + 1 }, () => {
      this.onSearch();
    });
  };

  onToggleQuestion = questionId => {
    const { expandedQuestions, showQuestionAnswers } = this.state;

    const newExpandedQuestions = expandedQuestions.includes(questionId)
      ? expandedQuestions.filter(qid => qid !== questionId)
      : [...expandedQuestions, questionId];

    const newShowQuestionAnswers = showQuestionAnswers.includes(questionId)
      ? showQuestionAnswers.filter(qid => qid !== questionId)
      : showQuestionAnswers;

    this.setState({
      expandedQuestions: newExpandedQuestions,
      showQuestionAnswers: newShowQuestionAnswers,
    });
  };

  onToggleQuestionAnswer = questionId => {
    const { expandedQuestions, showQuestionAnswers } = this.state;

    const newExpandedQuestions = expandedQuestions.includes(questionId)
      ? expandedQuestions
      : [...expandedQuestions, questionId];

    const newShowQuestionAnswers = showQuestionAnswers.includes(questionId)
      ? showQuestionAnswers.filter(qid => qid !== questionId)
      : [...showQuestionAnswers, questionId];

    this.setState({
      expandedQuestions: newExpandedQuestions,
      showQuestionAnswers: newShowQuestionAnswers,
    });
  };

  onToggleQuestionStats = questionId => {
    const { stats, serieses, questions } = this.props;
    const { expandedQuestions, showQuestionStats } = this.state;

    const question = questions.find(q => q.id === questionId);

    const newExpandedQuestions = expandedQuestions.includes(questionId)
      ? expandedQuestions
      : [...expandedQuestions, questionId];

    const newShowQuestionStats = showQuestionStats.includes(questionId)
      ? showQuestionStats.filter(qid => qid !== questionId)
      : [...showQuestionStats, questionId];

    if (!stats[question.series_id]) {
      const series = serieses.find(s => s.id === question.series_id);
      this.props.fetchStats({
        competitionId: series.competition_id,
        id: series.id,
      });
    }

    this.setState({
      expandedQuestions: newExpandedQuestions,
      showQuestionStats: newShowQuestionStats,
    });
  };

  renderTag = tag => {
    const { locale } = this.props.intl;
    const { selectedTags } = this.state;

    return (
      <button
        key={tag.id}
        type="button"
        className={classNames('btn', 'btn-secondary', 'mb-0', {
          'btn-primary': selectedTags.includes(tag.id),
          'btn-secondary': !selectedTags.includes(tag.id),
        })}
        onClick={() => this.onSelectTag(tag.id)}
      >
        {tag.localizedName(locale)}
      </button>
    );
  };

  renderQuestion = question => {
    const { locale } = this.props.intl;
    const { questionLocale } = this.props;

    const {
      competitions,
      serieses,
      choices,
      stats,
      printQuestions,
    } = this.props;

    const {
      expandedQuestions,
      showQuestionAnswers,
      showQuestionStats,
    } = this.state;

    const series = serieses.find(s => s.id === question.series_id);
    if (!series) return null;

    const competition = competitions.find(c => c.id === series.competition_id);
    if (!competition) return null;

    const questionChoices = question.choice_ids
      .map(choiceId => choices.find(c => c.id === choiceId))
      .filter(c => c);

    const questionNumber =
      series.question_ids.findIndex(questionId => questionId === question.id) +
      1;

    const isExpanded = expandedQuestions.includes(question.id);
    const showAnswer = showQuestionAnswers.includes(question.id);
    const showStats = showQuestionStats.includes(question.id);
    const shouldPrint = !!printQuestions.find(q => q.id === question.id);

    const answerSolvePercentage =
      stats[question.series_id] &&
      stats[question.series_id].answer_solve_percentages.find(
        asp => asp.question_id === question.id
      );

    const choiceAnswerPercentages =
      stats[question.series_id] &&
      stats[question.series_id].choice_answer_percentages.filter(
        cap => cap.question_id === question.id
      );

    return (
      <div key={question.id} className="row">
        <div className="col mb-5">
          <div className="row mb-3">
            <div className="col d-flex align-items-center">
              <h5 className="mb-0">
                <FormattedMessage
                  id="home-archive-search-question-title"
                  defaultMessage="{competition} / {series} / Kysymys #{number}"
                  values={{
                    competition: competition.name,
                    series: series.name,
                    number: questionNumber,
                  }}
                />
              </h5>
            </div>

            <div className="col-auto d-flex align-items-center">
              {question.tags.map(tag => (
                <span key={tag.id} className="badge badge-primary mr-1 mb-0">
                  {tag.localizedName(locale)}
                </span>
              ))}
            </div>

            <div className="col-auto ml-sm-auto d-flex">
              <div className="form-check mt-0 d-flex">
                <div className="mr-3" style={{ marginTop: '3px' }}>
                  <FormattedMessage
                    id="home-archive-search-question-choose-print"
                    defaultMessage="Valitse tulostettavaksi"
                  />
                </div>

                <label className="form-check-label">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    onChange={() => this.props.onToggleShouldPrint(question.id)}
                    checked={shouldPrint}
                  />

                  <span className="form-check-sign" />
                </label>
              </div>
            </div>
          </div>

          <div
            className={classNames('ml-3', {
              'overflow-fade': !isExpanded,
            })}
          >
            {(question.needsFallback(questionLocale) ||
              questionChoices.some(c => c.needsFallback(questionLocale))) && (
              <div className="text-danger mb-3">
                <i
                  className="fas fa-exclamation-circle mr-2"
                  style={{ marginBottom: '2px' }}
                />

                <FormattedMessage
                  id="question-missing-elements"
                  defaultMessage="Tästä kysymyksestä puuttuu osia haluamallasi kielellä"
                />
              </div>
            )}

            <MathJax.Text
              text={question.descriptionWithFallback(questionLocale)}
            />

            {isExpanded && (
              <div className="mt-4">
                {questionChoices.map((choice, index) => (
                  <div
                    key={choice.id}
                    className={classNames(
                      'HomeArchiveSearch-question-choice',
                      'row',
                      'border',
                      'mx-0',
                      'mb-2',
                      {
                        correct:
                          showAnswer && question.correct_choice === choice.id,
                      }
                    )}
                  >
                    <div className="col-auto d-flex align-items-center py-2 border-right">
                      <span>{String.fromCharCode(97 + index)}</span>
                    </div>

                    <div className="col py-2">
                      <MathJax.Text
                        text={choice.descriptionWithFallback(questionLocale)}
                      />
                    </div>
                  </div>
                ))}

                {showAnswer && (
                  <>
                    <div className="row mx-1 mt-4 mb-3 title-bg">
                      <div className="col d-flex align-items-center justify-content-center">
                        <h5 className="m-0 p-0 d-none d-md-block">
                          <FormattedMessage
                            id="home-archive-search-question-example"
                            defaultMessage="Esimerkkiratkaisu"
                          />
                        </h5>
                      </div>
                    </div>

                    <MathJax.Text
                      text={question.exampleWithFallback(questionLocale)}
                    />
                  </>
                )}

                {showStats && (
                  <>
                    <div className="row mx-1 mt-4 mb-3 title-bg">
                      <div className="col d-flex align-items-center justify-content-center">
                        <h5 className="m-0 p-0 d-none d-md-block">
                          <FormattedMessage
                            id="home-archive-search-question-stats"
                            defaultMessage="Tilastot"
                          />
                        </h5>
                      </div>
                    </div>

                    {stats[question.series_id] ? (
                      <div>
                        <p>
                          <FormattedHTMLMessage
                            id="home-archive-search-question-stats-asp"
                            defaultMessage="Kysymykseen vastasi <b>{answer}%</b> kilpailuun osallistuneista. Kysymyksen sai oikein <b>{solve}%</b> kilpailuun osallistuneista."
                            values={{
                              answer: answerSolvePercentage
                                ? Math.round(
                                    answerSolvePercentage.answer_percentage *
                                      100
                                  )
                                : '...',
                              solve: answerSolvePercentage
                                ? Math.round(
                                    answerSolvePercentage.solve_percentage * 100
                                  )
                                : '...',
                            }}
                          />
                        </p>

                        <p>
                          <FormattedMessage
                            id="home-archive-search-question-stats-cap"
                            defaultMessage="Vastaukset jakautuivat seuraavasti (oikea vastaus on tummennettu):"
                          />
                        </p>

                        <table className="table table-border">
                          <thead>
                            <tr>
                              {question.choice_ids.map((choiceId, index) => (
                                <th
                                  key={choiceId}
                                  className={classNames('text-center', {
                                    'font-weight-normal':
                                      question.correct_choice !== choiceId,
                                  })}
                                >
                                  {String.fromCharCode(97 + index)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              {question.choice_ids.map(choiceId => {
                                const cap = choiceAnswerPercentages.find(
                                  c => c.choice_id === choiceId
                                );

                                return (
                                  <td
                                    key={choiceId}
                                    className={classNames('text-center', {
                                      'font-weight-bold':
                                        question.correct_choice === choiceId,
                                    })}
                                  >
                                    {cap
                                      ? Math.round(cap.answer_percentage * 100)
                                      : '...'}
                                    %
                                  </td>
                                );
                              })}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <Spinner />
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="row mt-3">
            <div className="col-sm-auto">
              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={() => this.onToggleQuestion(question.id)}
              >
                {isExpanded ? (
                  <span>
                    <FormattedMessage
                      id="home-archive-search-question-collapse"
                      defaultMessage="Pienennä tehtävä"
                    />
                  </span>
                ) : (
                  <span>
                    <FormattedMessage
                      id="home-archive-search-question-expand"
                      defaultMessage="Näytä tehtävä"
                    />
                  </span>
                )}
              </button>
            </div>

            <div className="col-sm-auto">
              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={() => this.onToggleQuestionAnswer(question.id)}
              >
                {showAnswer ? (
                  <span>
                    <FormattedMessage
                      id="home-archive-search-question-solution-hide"
                      defaultMessage="Piilota ratkaisu"
                    />
                  </span>
                ) : (
                  <span>
                    <FormattedMessage
                      id="home-archive-search-question-solution-show"
                      defaultMessage="Näytä ratkaisu"
                    />
                  </span>
                )}
              </button>
            </div>

            <div className="col-sm-auto">
              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={() => this.onToggleQuestionStats(question.id)}
              >
                {showStats ? (
                  <span>
                    <FormattedMessage
                      id="home-archive-search-question-stats-hide"
                      defaultMessage="Piilota tilastot"
                    />
                  </span>
                ) : (
                  <span>
                    <FormattedMessage
                      id="home-archive-search-question-stats-show"
                      defaultMessage="Näytä tilastot"
                    />
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { locale, formatMessage } = this.props.intl;

    const {
      questions,
      questionsPagination,
      questionsLoading,
      tags,
      printQuestions,
    } = this.props;

    const { tagInputValue, page } = this.state;

    return (
      <div className="HomeArchiveSearch">
        <div className="HomeArchiveSearch-tag mt-4">
          <div className="row justify-content-between mb-3">
            <div className="col d-flex align-items-center">
              <div className="HomeArchiveSearch-tag-icon col-auto mr-4" />

              <h3 className="my-0">
                <FormattedMessage
                  id="home-archive-search-tag-title"
                  defaultMessage="Valitse tästä avainsanoja"
                />
              </h3>
            </div>

            <div className="col-sm-4 mt-4 mt-sm-0">
              <Autosuggest
                suggestions={tags.filter(
                  t =>
                    t
                      .localizedName(locale)
                      .toLocaleLowerCase()
                      .indexOf(tagInputValue.toLocaleLowerCase()) !== -1
                )}
                onSuggestionsFetchRequested={() => {}}
                onSuggestionsClearRequested={
                  this.onTagSuggestionsClearRequested
                }
                onSuggestionSelected={this.onTagSuggestionSelected}
                getSuggestionValue={t => t.id}
                shouldRenderSuggestions={() => true}
                focusInputOnSuggestionClick={false}
                renderSuggestion={t => (
                  <span className="text-capitalize">
                    {t.localizedName(locale)}
                  </span>
                )}
                inputProps={{
                  className: 'HomeArchiveSearch-tag-input form-control',
                  placeholder: formatMessage(messages.suggestionPlaceholder),
                  value: tagInputValue,
                  onChange: this.onTagInputChange,
                }}
              />
            </div>
          </div>

          <div>{[...tags, ...this.augmentedTags()].map(this.renderTag)}</div>
        </div>

        <hr />

        <div className="row mt-4 mb-5">
          <div className="col-auto d-flex">
            <div className="HomeArchiveSearch-questions-icon mr-4" />

            <div className="row">
              <div className="col-auto">
                <h3 className="my-0">
                  <FormattedMessage
                    id="home-archive-search-questions-title"
                    defaultMessage="Kysymykset"
                  />
                </h3>
              </div>

              <div className="col-auto d-flex align-items-start">
                <LocaleSelector isQuestion />
              </div>

              <div className="col-auto">{questionsLoading && <Spinner />}</div>
            </div>
          </div>

          <div className="col-auto ml-auto d-flex flex-column align-items-end">
            {printQuestions.length ? (
              <span className="mr-3">
                <FormattedMessage
                  id="home-archive-search-questions-print-count"
                  defaultMessage="{count} {count, plural, one {kysymys} other {kysymystä}} valittu tulostettavaksi"
                  values={{
                    count: printQuestions.length,
                  }}
                />
              </span>
            ) : (
              <span className="mr-3">
                <FormattedMessage
                  id="home-archive-search-questions-print-guide"
                  defaultMessage="Valitse listasta kysymyksiä tulostettavaksi"
                />
              </span>
            )}

            <div>
              <button
                type="button"
                className="btn btn-link mr-3 mb-0"
                onClick={this.props.onClearPrintQuestions}
                disabled={!printQuestions.length}
              >
                <FormattedMessage
                  id="home-archive-search-questions-print-clear"
                  defaultMessage="Tyhjennä"
                />
              </button>

              <button
                type="button"
                className="btn btn-primary mr-3 mb-0"
                onClick={this.props.onPrintSolutions}
                disabled={!printQuestions.length}
              >
                <i className="fas fa-print mr-3" />{' '}
                <FormattedMessage
                  id="home-archive-search-questions-print-solutions-button"
                  defaultMessage="Tulosta ratkaisuineen"
                />
              </button>

              <button
                type="button"
                className="btn btn-primary mb-0"
                onClick={this.props.onPrint}
                disabled={!printQuestions.length}
              >
                <i className="fas fa-print mr-3" />{' '}
                <FormattedMessage
                  id="home-archive-search-questions-print-button"
                  defaultMessage="Tulosta"
                />
              </button>
            </div>
          </div>
        </div>

        {questions.map(question => this.renderQuestion(question))}

        {!questions.length && (
          <FormattedMessage
            id="home-archive-search-questions-no-results"
            defaultMessage="Ei tuloksia"
          />
        )}

        <Pagination
          page={page}
          pagination={questionsPagination}
          onPageChange={this.onPageChange}
        />
      </div>
    );
  }
}

export default injectIntl(Search);
